import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { ScrollArea } from '../../ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  MAX_CANVAS_DROP_IMAGE_FILES,
  isSupportedCanvasImageFile,
} from '../utils/canvasImageFiles';
import {
  MAX_RAW_IMAGE_BYTES,
  MIN_IMAGE_DIMENSION,
} from '../../../domains/media/utils/imageProcessor';

type UploadFailure = { file: File; message: string };
type UploadIssue = { fileName: string; message: string };

export interface ImageUploadModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onUploadFiles: (files: File[]) => Promise<{ insertedCount: number; failures: UploadFailure[] }>;
  maxFiles?: number;
  title?: string;
  description?: string;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0B';
  const units = ['B', 'KB', 'MB', 'GB'] as const;
  const idx = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, idx);
  const digits = idx <= 1 ? 0 : 1;
  return `${value.toFixed(digits)}${units[idx]}`;
}

function dedupeFiles(files: File[]): File[] {
  const seen = new Set<string>();
  const out: File[] = [];
  for (const file of files) {
    const key = `${file.name}\0${file.size}\0${file.lastModified}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(file);
  }
  return out;
}

function collectSupportedFiles(
  fileList: FileList | null | undefined,
  maxFiles: number,
  skipMessages: {
    unsupportedFormat: string;
    exceedsMax: (maxLabel: string) => string;
    maxPerUpload: (limit: number) => string;
  },
): { files: File[]; skipped: UploadIssue[] } {
  const raw: File[] = [];
  if (fileList && fileList.length > 0) {
    for (let i = 0; i < fileList.length; i += 1) {
      const item = fileList.item(i);
      if (item) raw.push(item);
    }
  }
  const unique = dedupeFiles(raw);
  const skipped: UploadIssue[] = [];
  const supported = unique.filter((f) => {
    const ok = isSupportedCanvasImageFile(f);
    if (!ok) {
      skipped.push({ fileName: f.name, message: skipMessages.unsupportedFormat });
    }
    return ok;
  });
  const maxBytesLabel = formatBytes(MAX_RAW_IMAGE_BYTES);
  const withinSize = supported.filter((f) => {
    const ok = f.size <= MAX_RAW_IMAGE_BYTES;
    if (!ok) {
      skipped.push({ fileName: f.name, message: skipMessages.exceedsMax(maxBytesLabel) });
    }
    return ok;
  });
  const files = withinSize.slice(0, maxFiles);
  const overflow = withinSize.slice(maxFiles);
  overflow.forEach((f) => {
    skipped.push({ fileName: f.name, message: skipMessages.maxPerUpload(maxFiles) });
  });
  return { files, skipped };
}

export const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  open,
  onOpenChange,
  onUploadFiles,
  maxFiles = MAX_CANVAS_DROP_IMAGE_FILES,
  title,
  description,
}) => {
  const { t } = useTranslation('diagram');
  const resolvedTitle = title ?? t('imageUploadModal.title', { defaultValue: 'Upload image' });
  const resolvedDescription =
    description ?? t('imageUploadModal.description', { defaultValue: 'Insert images into your diagram.' });

  const skipMessages = useMemo(
    () => ({
      unsupportedFormat: t('imageUploadModal.errors.unsupportedFileFormat', {
        defaultValue: 'Unsupported file format.',
      }),
      exceedsMax: (maxLabel: string) =>
        t('imageUploadModal.errors.fileExceedsMax', {
          max: maxLabel,
          defaultValue: `File exceeds ${maxLabel}.`,
        }),
      maxPerUpload: (limit: number) =>
        t('imageUploadModal.errors.skippedMaxPerUpload', {
          count: limit,
          maxFiles: limit,
          defaultValue: `Skipped: max ${limit} image${limit === 1 ? '' : 's'} per upload.`,
        }),
    }),
    [t],
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [failures, setFailures] = useState<UploadFailure[]>([]);
  const [skippedIssues, setSkippedIssues] = useState<UploadIssue[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [insertedCount, setInsertedCount] = useState<number | null>(null);

  const reset = useCallback(() => {
    setSelectedFiles([]);
    setFailures([]);
    setSkippedIssues([]);
    setIsUploading(false);
    setInsertedCount(null);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const constraintsText = useMemo(() => {
    const rawLimit = Math.round(MAX_RAW_IMAGE_BYTES / (1024 * 1024));
    return {
      formats: 'JPEG, PNG, WebP',
      rawSize: t('imageUploadModal.maxFileSizeValue', {
        mb: rawLimit,
        defaultValue: `${rawLimit}MB max`,
      }),
      minDimensions: t('imageUploadModal.minDimensionsValue', {
        min: MIN_IMAGE_DIMENSION,
        defaultValue: `${MIN_IMAGE_DIMENSION}×${MIN_IMAGE_DIMENSION} min`,
      }),
    };
  }, [t]);

  const runUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setIsUploading(true);
    setFailures([]);
    setInsertedCount(null);
    try {
      const result = await onUploadFiles(files);
      setFailures(result.failures);
      setInsertedCount(result.insertedCount);
    } finally {
      setIsUploading(false);
    }
  }, [onUploadFiles]);

  const acceptFiles = useCallback(
    (fileList: FileList | null | undefined) => {
      const collected = collectSupportedFiles(fileList, maxFiles, skipMessages);
      setSkippedIssues(collected.skipped);
      setSelectedFiles(collected.files);
      void runUpload(collected.files);
    },
    [maxFiles, runUpload, skipMessages],
  );

  const handleSelectFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    acceptFiles(event.target.files);
    event.target.value = '';
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleDropFiles = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isUploading) return;
    acceptFiles(event.dataTransfer.files);
  };

  const isBusy = isUploading;
  const allIssues = useMemo<UploadIssue[]>(
    () => [
      ...skippedIssues,
      ...failures.map((f) => ({ fileName: f.file.name, message: f.message })),
    ],
    [failures, skippedIssues],
  );

  return (
    <Dialog open={open} onOpenChange={isBusy ? undefined : onOpenChange}>
      <DialogContent
        data-testid="tid.imageupload"
        className="sm:max-w-[640px] max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => { if (isBusy) e.preventDefault(); }}
        showCloseButton={!isBusy}
      >
        <DialogHeader>
          <DialogTitle>{resolvedTitle}</DialogTitle>
          <DialogDescription>
            {resolvedDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          <div
            data-testid="tid.imageupload.dropzone"
            className="rounded-md border border-dashed p-6 text-center"
            onDragEnter={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onDragOver={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onDrop={handleDropFiles}
          >
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              {isUploading ? (
                <Loader2 className="h-7 w-7 text-primary animate-spin" />
              ) : (
                <Upload className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div className="mt-3 text-sm font-medium">
              {t('imageUploadModal.dropzoneTitle', { defaultValue: 'Upload images' })}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {t('imageUploadModal.dropzoneHint', {
                defaultValue: 'Drag and drop images here, or choose files.',
              })}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple={maxFiles > 1}
              className="hidden"
              onChange={handleSelectFiles}
            />

            {selectedFiles.length === 0 ? (
              <div className="mt-4">
                <Button
                  data-testid="tid.imageupload.choosefile"
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isUploading}
                  onClick={handleOpenFilePicker}
                >
                  {t('imageUploadModal.chooseFile', { defaultValue: 'Choose file' })}
                </Button>
              </div>
            ) : (
              <div className="mt-4 text-left">
                <div className="mb-2 text-xs text-muted-foreground text-center">
                  {t('imageUploadModal.filesSelected', {
                    count: selectedFiles.length,
                  })}
                </div>
                <ScrollArea
                  type="always"
                  className={cn(
                    'rounded-md border bg-background',
                    selectedFiles.length > 1 ? 'h-[160px]' : 'h-[64px]',
                  )}
                >
                  <div className="divide-y">
                    {selectedFiles.map((file) => {
                      const failure = failures.find((f) => f.file === file || (f.file.name === file.name && f.file.size === file.size && f.file.lastModified === file.lastModified));
                      return (
                        <div key={`${file.name}-${file.size}-${file.lastModified}`} className="px-3 py-2 text-sm grid grid-cols-[minmax(0,1fr)_96px] gap-3 items-center">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{file.name}</div>
                            <div className="text-xs text-muted-foreground">{formatBytes(file.size)}</div>
                          </div>
                          <div
                            className={cn(
                              'text-xs font-medium text-center self-center',
                              isUploading ? 'text-muted-foreground' : failure ? 'text-destructive' : insertedCount !== null ? 'text-emerald-700' : 'text-muted-foreground',
                            )}
                          >
                            {isUploading
                              ? t('imageUploadModal.statusUploading', { defaultValue: 'Uploading' })
                              : failure
                                ? t('imageUploadModal.statusFailed', { defaultValue: 'Failed' })
                                : insertedCount !== null
                                  ? t('imageUploadModal.statusUploaded', { defaultValue: 'Uploaded' })
                                  : t('imageUploadModal.statusQueued', { defaultValue: 'Queued' })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            )}
            {isUploading && (
              <div className="mt-2 text-xs font-medium text-primary">
                {t('imageUploadModal.uploadingProgress', { defaultValue: 'Uploading images...' })}
              </div>
            )}
          </div>

          <div className="rounded-md border p-3">
            <div className="text-sm font-medium">
              {t('imageUploadModal.supported', { defaultValue: 'Supported' })}
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div>
                <span className="text-foreground">
                  {t('imageUploadModal.formatsLabel', { defaultValue: 'Formats:' })}
                </span>{' '}
                {constraintsText.formats}
              </div>
              <div>
                <span className="text-foreground">
                  {t('imageUploadModal.dimensionsLabel', { defaultValue: 'Dimensions:' })}
                </span>{' '}
                {constraintsText.minDimensions}
              </div>
              <div>
                <span className="text-foreground">
                  {t('imageUploadModal.fileSizeLabel', { defaultValue: 'File size:' })}
                </span>{' '}
                {constraintsText.rawSize}
              </div>
            </div>
          </div>

          {allIssues.length > 0 && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <div className="font-medium">
                {t('imageUploadModal.uploadErrorsTitle', { defaultValue: 'Upload errors' })}
              </div>
              <div className="mt-2 space-y-2 text-xs">
                {allIssues.map((issue, i) => (
                  <div key={`${issue.fileName}-${i}`} className="text-destructive flex items-start gap-1">
                    <span className="font-medium max-w-[220px] truncate" title={issue.fileName}>{issue.fileName}</span>
                    <span className="shrink-0">:</span>
                    <span className="min-w-0">{issue.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
            {t('imageUploadModal.done', { defaultValue: 'Done' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

